import { useState } from 'react';

import { logError } from '@zg-rentals/log-error';
import { Box, Divider, Heading, Paragraph, TextButton } from '@zillow/constellation-10';

import DebouncedButton from '@common/components/ConstellationDebouncedButton';
import Modal from '@common/components/ConstellationV10Modal';
import DetailsCard from '@rental-manager/components/DetailsCardConstellationV10';
import userPersonas from '@common/constants/userPersonas';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

interface Props {
  baseMonthlyAmount: Array<string>;
  checkIsPhoneVerificationNeeded: () => Promise<boolean>;
  currentProperty: ZrmClickstreamListing;
  depositAccount: Array<string>;
  editAccountInfo: () => void;
  editDueDate: () => void;
  editIdentityVerification: () => void;
  editMoveInCosts: () => void;
  editPaymentTerm: () => void;
  editRentAmount: () => void;
  editTenantInvitationInfo: () => void;
  featureFlagEnableMoveInCosts: boolean;
  goToPhoneVerificationPage: () => void;
  hasLandlordOnboarded: boolean;
  identityVerificationInfo: Record<string, any>;
  listingName: string;
  monthlyPaymentDate: Array<string>;
  moveInCosts: Array<string>;
  onBack: () => void;
  onClose: () => void;
  onSubmit: () => Promise<any>;
  paymentTerm: Array<string>;
  primaryTenant: Array<string>;
  showModalError: () => void;
  user: ZrmClickstreamUser;
}

const OnboardingConfirmationPage = ({
  baseMonthlyAmount,
  checkIsPhoneVerificationNeeded,
  currentProperty,
  depositAccount,
  editAccountInfo,
  editDueDate,
  editIdentityVerification,
  editMoveInCosts,
  editPaymentTerm,
  editRentAmount,
  editTenantInvitationInfo,
  goToPhoneVerificationPage,
  hasLandlordOnboarded,
  identityVerificationInfo,
  listingName = '',
  monthlyPaymentDate,
  moveInCosts,
  onBack: onBackProp,
  onClose: onCloseProp,
  onSubmit: onSubmitProp,
  paymentTerm,
  primaryTenant,
  showModalError,
  user,
}: Props) => {
  const [showLoadingScreen, setShowLoadingScreen] = useState<boolean>(false);

  const onSubmit = async (): Promise<any> => {
    IreneOnboardingCSEvents.emitContinueFromConfirmationPageEvent({ currentProperty, user });
    return Promise.resolve()
      .then(() => setShowLoadingScreen(true))
      .then(() => onSubmitProp())
      .then((error) => {
        if (error) {
          setShowLoadingScreen(false);
        }
        return error;
      });
  };

  const onBack = (): void => {
    IreneOnboardingCSEvents.emitBackFromOnboardingFlowEvent({
      currentProperty,
      user,
      triggerObject: 'invite_tenant',
    });
    onBackProp();
  };

  const onClose = (): void => {
    IreneOnboardingCSEvents.emitExitFromOnboardingFlowEvent({
      currentProperty,
      user,
      triggerObject: 'invite_tenant',
    });
    onCloseProp();
  };

  const getTaxVerification = () => {
    const {
      firstName,
      lastName,
      street,
      city,
      state: addressState,
      zipCode,
      phoneNumber,
      monthOfBirth,
      dayOfBirth,
      yearOfBirth,
      fullSocialSecurityNumber,
      businessName,
      dbaName,
      employerIdentificationNumber,
      businessStreet,
      businessCity,
      businessState,
      businessZipCode,
      selectedTaxVerificationType,
    } = identityVerificationInfo;

    // Note: yearOfBirth, monthOfBirth and dayOfBirth are all strings
    const birthDate = new Date(yearOfBirth, monthOfBirth - 1, dayOfBirth);
    const birthDateFormatted = birthDate.toLocaleString('en-us', {
      month: 'long',
      year: 'numeric',
      day: '2-digit',
    });

    const last4DigitSSN = fullSocialSecurityNumber?.slice(-4);
    if (selectedTaxVerificationType === 'single_member_llc') {
      return (
        <>
          <Paragraph>Single LLC</Paragraph>

          <Paragraph>{businessName}</Paragraph>
          <Paragraph>DBA: {dbaName || 'N/A'}</Paragraph>
          <Paragraph>EIN: {employerIdentificationNumber}</Paragraph>
          <Paragraph>
            {firstName} {lastName}
          </Paragraph>
          <Paragraph>{street}</Paragraph>
          <Paragraph>
            {city}, {addressState} {zipCode}
          </Paragraph>
          <Paragraph>{phoneNumber}</Paragraph>
          <Paragraph>Date of birth: {birthDateFormatted}</Paragraph>
          <Paragraph>Last 4 digits of SSN: XXX-XX-{last4DigitSSN}</Paragraph>
        </>
      );
    } else if (selectedTaxVerificationType === 'multi_member_llc') {
      return (
        <>
          <Paragraph>Multi LLC or corporation</Paragraph>
          <Paragraph>Business:</Paragraph>
          <Paragraph>{businessName}</Paragraph>
          <Paragraph>DBA: {dbaName || 'N/A'}</Paragraph>
          <Paragraph>EIN: {employerIdentificationNumber}</Paragraph>
          <Paragraph>{businessStreet}</Paragraph>
          <Paragraph>
            {businessCity}, {businessState} {businessZipCode}
          </Paragraph>
          <Paragraph>Owner:</Paragraph>
          <Paragraph>
            {firstName} {lastName}
          </Paragraph>
          <Paragraph>{street}</Paragraph>
          <Paragraph>
            {city}, {addressState} {zipCode}
          </Paragraph>
          <Paragraph>{phoneNumber}</Paragraph>
          <Paragraph>Date of birth: {birthDateFormatted}</Paragraph>
          <Paragraph>Last 4 digits of SSN: XXX-XX-{last4DigitSSN}</Paragraph>
        </>
      );
    } else {
      return (
        <Box>
          <Paragraph>Individual</Paragraph>
          <Paragraph>
            {firstName} {lastName}
          </Paragraph>
          <Paragraph>{street}</Paragraph>
          <Paragraph>
            {city}, {addressState} {zipCode}
          </Paragraph>
          <Paragraph>{phoneNumber}</Paragraph>
          <Paragraph>Date of birth: {birthDateFormatted}</Paragraph>
          <Paragraph>Last 4 digits of SSN: XXX-XX-{last4DigitSSN}</Paragraph>
        </Box>
      );
    }
  };

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      onRequestClose={onClose}
      header={
        <Box css={{ padding: 'token(spacing.sm) !important' }}>
          <TextButton textStyle="body-sm-bold" tone="brand" css={{ float: 'left' }} onClick={onBack}>
            Back
          </TextButton>
          <Heading level={3} textStyle="heading-xs" css={{ display: 'flex', justifyContent: 'center' }}>
            Payment setup
          </Heading>
        </Box>
      }
      body={
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto',
            gap: 'token(spacing.sm) !important',
            padding: 'token(spacing.lg) !important',
          }}
        >
          <Box>
            <Heading level={5}>Confirm info and invite tenant</Heading>
            <Paragraph textStyle="body-lg">
              Please review the information you provided for {listingName} and confirm it&apos;s correct.
            </Paragraph>
          </Box>

          <Box>
            <Divider />
            <DetailsCard className="fs-exclude" title="Monthly rent" onEdit={editRentAmount}>
              {baseMonthlyAmount}
            </DetailsCard>
          </Box>

          <Box>
            <Divider />
            <DetailsCard className="fs-exclude" title="Monthly payment date" onEdit={editDueDate}>
              {monthlyPaymentDate}
            </DetailsCard>
          </Box>

          <Box>
            <Divider />
            <DetailsCard className="fs-exclude" title="Payment period" onEdit={editPaymentTerm}>
              {paymentTerm}
            </DetailsCard>
          </Box>

          <Box>
            <Divider />
            <DetailsCard className="fs-exclude" title="Move-in costs" onEdit={editMoveInCosts}>
              {moveInCosts}
            </DetailsCard>
          </Box>

          <Box>
            <Divider />
            <DetailsCard title="Primary tenant" className="fs-exclude txtWrap" onEdit={editTenantInvitationInfo}>
              {primaryTenant}
            </DetailsCard>
          </Box>

          {!hasLandlordOnboarded && (
            <Box>
              <Divider />
              <DetailsCard className="fs-exclude" title="Tax information" onEdit={editIdentityVerification}>
                {getTaxVerification()}
              </DetailsCard>
            </Box>
          )}

          <Box>
            <Divider />
            <DetailsCard
              className="fs-exclude"
              title="Deposit account"
              onEdit={() => {
                checkIsPhoneVerificationNeeded()
                  .then((isPhoneVerificationNeeded) => {
                    if (isPhoneVerificationNeeded) {
                      return goToPhoneVerificationPage();
                    } else {
                      return editAccountInfo();
                    }
                  })
                  .catch((error) => {
                    logError({ error, context: 'OnboardingConfirmationPage - DetailsCard - onEdit' });
                    return showModalError();
                  });
              }}
            >
              {depositAccount}
            </DetailsCard>
          </Box>
        </Box>
      }
      footer={
        <Box css={{ padding: 'token(spacing.sm) !important' }}>
          <DebouncedButton onClick={onSubmit} loading={showLoadingScreen}>
            Confirm and invite tenant
          </DebouncedButton>
        </Box>
      }
    />
  );
};

export default OnboardingConfirmationPage;
